import React, { useState } from 'react';
import styles from './Customers.module.css';
import customerData from '../../data/customerData.json';
import { Tooltip, Pagination } from '@mui/material';

// Import Icons
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Define the structure of a customer
interface Customer {
    id: number;
    customerCode: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    status: string;
    totalOrders: number;
    totalRevenue: number;
    joinDate: string;
    lastPurchase: string;
}

const Customers: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>(customerData);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = customers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(customers.length / itemsPerPage);

    const handleSearch = () => {
        console.log('Searching customers...', { searchTerm, statusFilter });
    };

    const handleReset = () => {
        setSearchTerm('');
        setStatusFilter('');
    };

    const handleAddCustomer = () => {
        console.log('Add new customer');
    };

    const handleViewCustomer = (id: number) => {
        console.log('View customer:', id);
    };

    const handleEditCustomer = (id: number) => {
        console.log('Edit customer:', id);
    };

    const handleDeleteCustomer = (id: number) => {
        console.log('Delete customer:', id);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Function to get status badge class
    const getStatusClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return styles.success;
            case 'inactive':
                return styles.danger;
            case 'on hold':
                return styles.warning;
            default:
                return styles.default;
        }
    };

    // Function to format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className={styles.container}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h2>Customers</h2>
                    <p>Create and manage customers</p>
                </div>
                <div className={styles.actionsSection}>
                    <button className={styles.actionButton}>
                        <FileDownloadIcon fontSize="small"/> Export Customers
                    </button>
                    <button 
                        className={`${styles.actionButton} ${styles.addButton}`}
                        onClick={handleAddCustomer}
                    >
                        <PersonAddIcon fontSize="small"/> Add Customer
                    </button>
                </div>
            </div>

            {/* Filter Area */}
            <div className={styles.filterArea}>
                <div className={styles.filterGroup}>
                    <label>Status</label>
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Search</label>
                    <input
                        type="text"
                        placeholder="Customer Name / Code"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className={styles.filterActions}>
                    <button 
                        className={`${styles.actionButton} ${styles.searchButton}`}
                        onClick={handleSearch}
                    >
                        <SearchIcon fontSize="small"/> Search
                    </button>
                    <button 
                        className={`${styles.actionButton} ${styles.resetButton}`}
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Customer Code</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Total Orders</th>
                            <th>Total Revenue</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((customer, index) => (
                            <tr key={customer.id}>
                                <td>{indexOfFirstItem + index + 1}</td>
                                <td>{customer.customerCode}</td>
                                <td>{customer.name}</td>
                                <td>{customer.email}</td>
                                <td>{customer.totalOrders}</td>
                                <td>{formatCurrency(customer.totalRevenue)}</td>
                                <td>
                                    <span className={`${styles.statusBadge} ${styles[customer.status.toLowerCase()]}`}>
                                        {customer.status}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actionIcons}>
                                        <Tooltip title="View Details">
                                            <VisibilityIcon 
                                                fontSize="small"
                                                onClick={() => handleViewCustomer(customer.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Edit Customer">
                                            <EditIcon 
                                                fontSize="small"
                                                onClick={() => handleEditCustomer(customer.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Delete Customer">
                                            <DeleteIcon 
                                                fontSize="small"
                                                onClick={() => handleDeleteCustomer(customer.id)}
                                            />
                                        </Tooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={styles.paginationContainer}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </div>
        </div>
    );
};

export default Customers; 